import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { addReportTemplate, editReportTemplate, getReportTemplate } from "services/ReportTemplates.services";
import { toast } from "sonner";
import { z } from "zod";

import { ROUTES } from "utils/constants";
import { queryClient } from "utils/queryClient";

import { buildReportTemplateSchema } from "./ReportTemplate.schema";

export function useReportTemplate() {
  const [isCancelOpen, setIsCancelOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const { t } = useTranslation();
  const reportTemplateSchema = buildReportTemplateSchema(t);

  // ====================================================================================
  // ADD REPORT TEMPLATE
  // ====================================================================================
  const addReportTemplateMutation = useMutation({
    mutationFn: addReportTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reportTemplates"] });
      toast.success(t("profile.reportTemplates.addReportTemplateSuccess"));
      navigate(ROUTES.PROFILE_REPORT_TEMPLATES, { replace: true });
    },
    onError: () => {
      toast.error(t("profile.reportTemplates.addReportTemplateError"));
    },
  });

  // ====================================================================================
  // EDIT REPORT TEMPLATE
  // ====================================================================================
  const editReportTemplateMutation = useMutation({
    mutationFn: (data: z.infer<typeof reportTemplateSchema>) => editReportTemplate(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reportTemplates"] });
      queryClient.invalidateQueries({ queryKey: ["reportTemplate", id] });
      navigate(ROUTES.PROFILE_REPORT_TEMPLATES, { replace: true });
      toast.success(t("profile.reportTemplates.editReportTemplateSuccess"));
    },
    onError: () => {
      toast.error(t("profile.reportTemplates.editReportTemplateError"));
    },
  });

  // ====================================================================================
  // GET REPORT TEMPLATE
  // ====================================================================================
  const { data: reportTemplate, isLoading: isLoadingReportTemplate } = useQuery({
    queryKey: ["reportTemplate", id],
    queryFn: () => getReportTemplate(id!),
    enabled: !!id,
  });

  // ====================================================================================
  // FORM
  // ====================================================================================
  const form = useForm<z.infer<typeof reportTemplateSchema>>({
    resolver: zodResolver(reportTemplateSchema),
    defaultValues: {
      name: "",
      description: "",
      content: "",
    },
  });

  function onSubmit(values: z.infer<typeof reportTemplateSchema>) {
    if (id) {
      editReportTemplateMutation.mutate(values);
      return;
    }
    addReportTemplateMutation.mutate(values);
  }

  // ====================================================================================
  // HANDLERS
  // ====================================================================================
  const handleCancel = () => {
    if (form.formState.isDirty) {
      setIsCancelOpen(true);
      return;
    } else {
      navigate(ROUTES.PROFILE_REPORT_TEMPLATES, { replace: true });
    }
  };

  // ====================================================================================
  // EFFECTS
  // ====================================================================================
  useEffect(() => {
    if (reportTemplate) {
      form.reset({
        name: reportTemplate.name,
        description: reportTemplate.description,
        content: reportTemplate.content,
      });
    }
  }, [reportTemplate, form]);

  return {
    form,
    onSubmit,
    handleSubmit: form.handleSubmit,
    isLoadingReportTemplate,
    handleCancel,
    isCancelOpen,
    setIsCancelOpen,
  };
}
